"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  blockedClaimCopy,
  claimErrorMessage,
  parseRecoveryWindow,
  type BlockedClaimCopy,
} from "@/lib/draft-projects/claim-reasons";
import { ClaimBlockedPanel } from "@/components/funnel/claim-blocked-panel";
import { ClaimPausedPanel } from "@/components/funnel/claim-paused-panel";
import { retryAfterSeconds } from "@/lib/retry-after";

export function MergeDraft({
  sessionToken,
  title,
}: {
  sessionToken: string;
  title: string | null;
}) {
  const router = useRouter();
  const [claiming, setClaiming] = useState(false);
  const [finalisingPccp, setFinalisingPccp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedClaimCopy | null>(null);
  const [paused, setPaused] = useState<{
    sessionToken: string;
    projectId: string;
    retryAfterSeconds: number;
    retryAt: number;
  } | null>(null);
  const [claimRetryEnabled, setClaimRetryEnabled] = useState(true);
  const operationGeneration = useRef(0);
  const previousSessionToken = useRef(sessionToken);

  useEffect(() => {
    if (previousSessionToken.current !== sessionToken) {
      previousSessionToken.current = sessionToken;
      setPaused(null);
      setBlocked(null);
      setError(null);
      setClaiming(false);
      setFinalisingPccp(false);
      setClaimRetryEnabled(true);
    }
  }, [sessionToken]);

  useLayoutEffect(() => {
    return () => {
      operationGeneration.current += 1;
    };
  }, [sessionToken]);

  async function claim() {
    const operation = ++operationGeneration.current;
    const isCurrent = () => operationGeneration.current === operation;
    setClaiming(true);
    setFinalisingPccp(false);
    setError(null);
    if (!paused) setBlocked(null);
    try {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (!isCurrent()) return;
        const res = await fetch(`/api/draft-projects/${sessionToken}/claim`, {
          method: "POST",
        });
        if (!isCurrent()) return;
        const data = (await res.json()) as {
          redirect?: string;
          error?: string;
          projectId?: string;
          recoverUntil?: unknown;
          readOnlyReason?: unknown;
        };
        if (!isCurrent()) return;
        if (res.ok && data.redirect) {
          router.push(data.redirect);
          return;
        }
        if (
          res.status === 429 &&
          data.error === "draft_pcpp_processing" &&
          typeof data.projectId === "string"
        ) {
          const delay = retryAfterSeconds(res.headers.get("Retry-After"));
          setPaused({
            sessionToken,
            projectId: data.projectId,
            retryAfterSeconds: delay,
            retryAt: Date.now() + delay * 1000,
          });
          setClaimRetryEnabled(true);
          setClaiming(false);
          setFinalisingPccp(false);
          return;
        }
        if (data.error === "draft_pcpp_processing" && attempt < 89) {
          setFinalisingPccp(true);
          await new Promise((resolve) => window.setTimeout(resolve, 2500));
          if (!isCurrent()) return;
          continue;
        }
        // Account-state denials (plan limit, cancelled, role) are terminal
        // until the account changes, so they get the explanation + a real next
        // step instead of a retryable error. THIS is the door a signed-in
        // Starter user at their limit comes through, so it is the one that
        // matters most for issue #2103 — before the shared copy module it
        // rendered the raw `project_limit_reached` token in red.
        const blockedCopy = blockedClaimCopy(
          data.error,
          parseRecoveryWindow(data.recoverUntil),
          // past_due vs cancelled read-only copy (#2716 review F8).
          data.readOnlyReason,
        );
        if (blockedCopy) {
          setBlocked(blockedCopy);
          setClaimRetryEnabled(false);
          setClaiming(false);
          setFinalisingPccp(false);
          return;
        }
        setError(claimErrorMessage(data.error));
        setClaimRetryEnabled(false);
        setClaiming(false);
        setFinalisingPccp(false);
        return;
      }
      throw new Error("The PCCP is still being checked. Please try again.");
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : "Something went wrong");
      setClaimRetryEnabled(true);
      setClaiming(false);
      setFinalisingPccp(false);
    }
  }

  if (paused?.sessionToken === sessionToken) {
    return (
      <ClaimPausedPanel
        projectId={paused.projectId}
        retryAfterSeconds={paused.retryAfterSeconds}
        retryAt={paused.retryAt}
        onRetry={claim}
        retrying={claiming}
        retryEnabled={claimRetryEnabled && !blocked}
        message={blocked?.body ?? error}
      />
    );
  }

  if (blocked) {
    // The SHARED panel — same copy, same new-tab CTA, same live retry as the
    // post-signup callback door. See its file header: these two screens have
    // now drifted apart twice, so they render one component rather than two
    // implementations kept in step by review.
    return <ClaimBlockedPanel copy={blocked} onRetry={claim} />;
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <h1 className="font-heading text-2xl font-extrabold text-navy">Save this draft to your account</h1>
      <p className="mt-3 text-sm leading-6 text-muted">
        {finalisingPccp
          ? "The original PCCP is saved. We are finishing the PDF check before opening your project."
          : `You are signed in and have an unfinished draft${title ? ` for ${title}` : ""}. Claim it now to keep working in your project workspace.`}
      </p>
      {error ? <p className="mt-4 text-sm font-semibold text-danger">{error}</p> : null}
      <button
        type="button"
        onClick={claim}
        disabled={claiming}
        className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark disabled:opacity-50"
      >
        {finalisingPccp ? "Finalising PCCP..." : claiming ? "Claiming..." : "Claim and continue"}
      </button>
    </div>
  );
}

